import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode
} from "@angular/common/http";
import { inject, Injectable, Injector } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError
} from "rxjs";
import { environment } from "../../../../environments/environment";
import { AuthService } from "../../services/auth/auth.service";
import { TokenService } from "../../services/token/token.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshDoneSubject = new BehaviorSubject<boolean>(false);
  private readonly tokenService = inject(TokenService);
  private readonly injector = inject(Injector);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const accessToken = this.tokenService.getAccessToken();
    let authReq = req;

    const isOurBackend = req.url.startsWith(environment.backendUrl);
    if (accessToken && isOurBackend) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`
        }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.Unauthorized
        ) {
          return this.handleRefreshToken(req, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handleRefreshToken(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshDoneSubject.pipe(
        filter((value) => value === true),
        take(1),
        switchMap(() => {
          const newToken = this.tokenService.getAccessToken();
          const newReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`
            }
          });
          return next.handle(newReq);
        })
      );
    } else {
      const refreshToken = this.tokenService.getRefreshToken();
      if (!refreshToken) return throwError(() => new Error("No refresh token"));

      this.isRefreshing = true;
      this.refreshDoneSubject.next(false);

      return this.tokenService.refreshAccessToken(refreshToken).pipe(
        catchError((err) => {
          this.isRefreshing = false;

          const authService = this.injector.get(AuthService);
          authService.logout();

          return throwError(() => err);
        }),
        switchMap((newAccessToken) => {
          this.isRefreshing = false;
          this.refreshDoneSubject.next(true);

          const newReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newAccessToken}`
            }
          });
          return next.handle(newReq);
        })
      );
    }
  }
}
